import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";
import styles from "./styles.module.css";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Dropdown, DropdownItem } from "../../../components/ui/dropdown";
import Button from "../../../components/button-v1";
import Spinner from "../../../components/spinner";
import { handleApiError } from "../../../utils/handleApiError";
import {
  getLocationPlacesOfService,
  updatePlaceOfService,
} from "../../../services/place-of-service";
import AddPlaceOfServiceModal from "./addPlaceOfServiceModal";

// Admin › Locations › Places of Service: lists a location's places of
// service (its own entry first, locked as "Default") and lets admins add,
// rename and switch them off / on.
const PlacesOfServiceModal = ({ isOpen, onClose, location, onChanged }) => {
  const queryClient = useQueryClient();
  // null = closed, {} = add, { placeOfService } = rename
  const [formState, setFormState] = useState(null);

  const { data, isLoading } = useQuery(
    ["places-of-service", location?.id, "all"],
    () => getLocationPlacesOfService(location.id),
    {
      enabled: isOpen && Boolean(location?.id),
      onError: handleApiError,
    }
  );
  const placesOfService = data?.places_of_service || [];

  const toggleMutation = useMutation(
    place => updatePlaceOfService(place.id, { active: !place.active }),
    {
      onSuccess: (_, place) => {
        queryClient.invalidateQueries(["places-of-service", location.id]);
        toast.success(
          place.active
            ? "Place of service deactivated successfully"
            : "Place of service activated successfully"
        );
        onChanged?.();
      },
      // A switch-off is refused while patients still have it; the backend
      // message includes the patient count.
      onError: handleApiError,
    }
  );

  const handleRowAction = (action, place) => {
    if (action === "rename") {
      setFormState({ placeOfService: place });
    } else if (action === "toggle") {
      toggleMutation.mutate(place);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} setOpen={onClose}>
        {/* Escape would otherwise close this modal along with the Add /
            Rename modal stacked on top of it */}
        <DialogContent className={styles.modal} closeDisabled={Boolean(formState)}>
          <DialogHeader>
            <DialogTitle className={styles.title}>Places of Service</DialogTitle>
          </DialogHeader>
          <DialogBody className={styles.body}>
            <div className={styles.location_row}>
              <p className={styles.location_name}>
                {data?.location?.name || location?.name}
              </p>
              <Button
                variant="primary"
                className={styles.add_btn}
                onClick={() => setFormState({})}
              >
                + Add
              </Button>
            </div>

            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Patients</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className={styles.loading_cell}>
                      <Spinner />
                    </td>
                  </tr>
                ) : (
                  placesOfService.map(place => (
                    <tr key={place.id}>
                      <td>{place.name}</td>
                      <td>
                        <span
                          className={`${styles.status} ${place.active
                            ? styles.status_active
                            : styles.status_inactive
                            }`}
                        >
                          {place.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{place.patients_count ?? 0}</td>
                      <td>
                        {place.is_location_default ? (
                          "Default"
                        ) : (
                          <Dropdown
                            align="right"
                            className={styles.actions_menu}
                            disabled={toggleMutation.isLoading}
                            onChange={action => handleRowAction(action, place)}
                            trigger={
                              <button
                                type="button"
                                className={styles.actions_trigger}
                                title="Actions"
                              >
                                <MoreVertical size={20} style={{ flexShrink: 0 }} />
                              </button>
                            }
                          >
                            <DropdownItem
                              value="rename"
                              className={styles.actions_item}
                            >
                              Rename
                            </DropdownItem>
                            <DropdownItem
                              value="toggle"
                              className={styles.actions_item}
                            >
                              {place.active ? "Deactivate" : "Activate"}
                            </DropdownItem>
                          </Dropdown>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {formState && (
        <AddPlaceOfServiceModal
          isOpen={Boolean(formState)}
          onClose={() => setFormState(null)}
          location={data?.location || location}
          placeOfService={formState.placeOfService}
          existingPlaces={placesOfService}
          onSaved={onChanged}
        />
      )}
    </>
  );
};

export default PlacesOfServiceModal;
